import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ImageOff, Check, ZoomIn, Pencil, Trash2 } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { mediaUrl, IMG } from '../lib/mediaUrl';
import { useSeo } from '../lib/useSeo';
import { deleteProduct } from '../lib/deleteProduct';
import { useAuth } from '../context/AuthContext';
import { track } from '../lib/track';
import { useCart } from '../context/CartContext';
import Lightbox from '../components/Lightbox';
import ConfirmDialog from '../components/ConfirmDialog';
import WishlistButton from '../components/WishlistButton';

// Minimal product view (Chase 2026-07-22): the 3D holo-slab viewer and the
// click-spark effect are gone. A clean image with thumbnails and a plain buy
// panel — nothing tilts, sparks, or downloads three.js.
function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Below the state it reads, not above it: declared earlier this referenced
  // `product` in its own dependency array before the useState ran, which is a
  // temporal dead zone error and blanked the whole page.
  useEffect(() => {
    if (!product?.id) return;
    track('product_view', {
      label: product.name,
      product_id: product.id,
      value_cents: product.price_cents ?? null,
    });
  }, [product?.id, product?.name, product?.price_cents]);

  const handleDelete = async () => {
    if (!product) return;
    setDeleting(true);
    setDeleteError('');
    const { error } = await deleteProduct(product.id);
    setDeleting(false);
    if (error) { setDeleteError(error.message); return; }
    navigate('/shop');
  };

  useEffect(() => {
    setLoading(true);
    setAdded(false);
    setActivePhoto(0);
    // Explicit column list, not select('*') — cost_cents is internal-only
    // (what we paid, never shown to shoppers) and RLS is row-level, not
    // column-level, so a select('*') here would ship it straight to the
    // browser for anyone to read off the network tab.
    supabase.from('products').select('id, name, slug, description, price_cents, quantity_available, condition, grade, rarity, number, set_name, product_media(storage_path, sort_order)').eq('slug', slug).eq('status', 'active').maybeSingle()
      .then(({ data }) => { setProduct(data); setLoading(false); });
  }, [slug]);

  // Per-page SEO / tab title. Only meaningful once the product has loaded;
  // useSeo skips undefined fields, so passing empties while loading is a no-op.
  // (Crawler caveat: JS-run bots like Google get this; non-JS social bots see
  // the static site-wide tags in index.html — see src/lib/useSeo.js.)
  const seoFirstPhoto = product
    ? (product.product_media || [])
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((m) => mediaUrl(m.storage_path, IMG.card))[0]
    : null;
  const seoDescription = product
    ? (product.description
        ? product.description.trim().slice(0, 160)
        : `${product.name} — available now at Shiny Vault LGS. Pokemon & TCG cards, sealed product, and singles, shipped fast.`)
    : undefined;
  useSeo({
    title: product ? `${product.name} · Shiny Vault LGS` : undefined,
    description: seoDescription,
    image: seoFirstPhoto || (product ? 'https://shinyvaultlgs.com/og-image.jpg' : undefined),
    url: product ? `https://shinyvaultlgs.com/product/${slug}` : undefined,
  });

  if (loading) return <div className="sv-container" style={{ padding: 60 }}>Loading…</div>;
  if (!product) {
    return (
      <div className="sv-container" style={{ padding: 60, textAlign: 'center' }}>
        <p>That listing isn't available.</p>
        <Link to="/shop" className="sv-btn" style={{ marginTop: 14, display: 'inline-flex' }}>Back to shop</Link>
      </div>
    );
  }

  const mediaPaths = (product.product_media || [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((m) => m.storage_path);
  // Three sizes off the same photos: the big image only ever paints ~600px
  // wide, the strip 56px, and the lightbox is where someone checks a slab's
  // corners, so that one stays large.
  const photos = mediaPaths.map((path) => mediaUrl(path, IMG.hero));
  const thumbs = mediaPaths.map((path) => mediaUrl(path, IMG.thumb));
  const zoomPhotos = mediaPaths.map((path) => mediaUrl(path, IMG.zoom));

  const soldOut = product.quantity_available <= 0;

  const handleAdd = () => {
    addItem({ ...product, image_url: photos[0] || null }, quantity);
    setAdded(true);
  };

  return (
    <div className="sv-container sv-product-detail sv-page-top sv-page-bottom">
      <div>
        <button
          type="button"
          onClick={() => photos.length > 0 && setLightboxOpen(true)}
          aria-label={photos.length > 0 ? 'Zoom in for quality inspection' : undefined}
          style={{
            aspectRatio: '1', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-muted)', background: 'var(--bg-elevated)', overflow: 'hidden',
            borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: 0,
            position: 'relative', cursor: photos.length > 0 ? 'zoom-in' : 'default',
          }}
        >
          {photos.length > 0 ? (
            <>
              <img src={photos[activePhoto]} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 16 }} />
              <span
                style={{
                  position: 'absolute', bottom: 10, right: 10, display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700,
                  background: 'color-mix(in srgb, var(--bg) 78%, transparent)', color: 'var(--text)',
                  border: '1px solid var(--border)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                }}
              >
                <ZoomIn size={13} /> Zoom
              </span>
            </>
          ) : (
            <ImageOff size={48} />
          )}
        </button>
        {photos.length > 1 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {thumbs.map((url, i) => (
              <button
                key={url}
                onClick={() => setActivePhoto(i)}
                style={{
                  width: 56, height: 56, padding: 0, borderRadius: 8, overflow: 'hidden',
                  border: i === activePhoto ? '2px solid var(--accent)' : '1px solid var(--border)', background: 'none',
                }}
              >
                <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 900, marginBottom: 8 }}>{product.name}</h1>
        <div style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: 18 }}>{money(product.price_cents)}</div>

        {(() => {
          const conditionText = product.condition === 'Graded' && product.grade
            ? `Graded · ${product.grade}`
            : product.condition;
          const specs = [
            { label: 'Condition', value: conditionText },
            { label: 'Rarity', value: product.rarity },
            { label: 'Set', value: product.set_name },
            { label: 'Number', value: product.number },
          ].filter((s) => s.value);
          if (specs.length === 0) return null;
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              {specs.map((s) => (
                <div
                  key={s.label}
                  style={{
                    display: 'inline-flex', alignItems: 'baseline', gap: 6,
                    padding: '5px 12px', borderRadius: 999,
                    background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                  }}
                >
                  <span style={{ fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--text-muted)' }}>{s.label}</span>
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)' }}>{s.value}</span>
                </div>
              ))}
            </div>
          );
        })()}

        {isAdmin && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            <button
              type="button"
              onClick={() => navigate(`/admin/products/${product.id}`)}
              className="sv-btn-outline"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', fontSize: '0.85rem' }}
            >
              <Pencil size={15} /> Edit
            </button>
            <button
              type="button"
              onClick={() => { setDeleteError(''); setConfirmingDelete(true); }}
              className="sv-btn-outline"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', fontSize: '0.85rem', color: 'var(--danger)', borderColor: 'var(--danger)' }}
            >
              <Trash2 size={15} /> Delete
            </button>
          </div>
        )}

        {product.description && (
          <p style={{ color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 24 }}>{product.description}</p>
        )}

        {soldOut ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'flex-start' }}>
            <div className="sv-card" style={{ padding: 16, color: 'var(--danger)', fontWeight: 700, width: '100%' }}>
              Sold out
            </div>
            <WishlistButton product={product} variant="inline" />
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <input
                type="number"
                min={1}
                max={product.quantity_available}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Math.min(product.quantity_available, parseInt(e.target.value, 10) || 1)))}
                className="sv-input"
                style={{ width: 70, textAlign: 'center' }}
              />
              <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{product.quantity_available} in stock</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button onClick={handleAdd} className="sv-btn" style={{ padding: '14px 30px' }}>
                {added ? <><Check size={16} /> Added to cart</> : 'Add to cart'}
              </button>
              {added && (
                <button onClick={() => navigate('/cart')} className="sv-btn-outline" style={{ padding: '14px 28px' }}>
                  View cart
                </button>
              )}
            </div>
            <div style={{ marginTop: 12 }}>
              <WishlistButton product={product} variant="inline" />
            </div>
          </>
        )}
      </div>

      {lightboxOpen && photos.length > 0 && (
        <Lightbox
          photos={zoomPhotos}
          startIndex={activePhoto}
          onIndexChange={setActivePhoto}
          alt={product.name}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {confirmingDelete && (
        <ConfirmDialog
          title="Delete this item?"
          message={
            deleteError
              ? `Couldn't delete: ${deleteError}`
              : `"${product.name}" will be permanently removed from the shop, along with its photos. Past orders that included it are kept. This can't be undone.`
          }
          confirmLabel="Delete item"
          busy={deleting}
          onConfirm={handleDelete}
          onCancel={() => { if (!deleting) { setConfirmingDelete(false); setDeleteError(''); } }}
        />
      )}
    </div>
  );
}
